import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { MdRefresh } from 'react-icons/md';
import axios from '../lib/axios';
import { endpoints } from '../constants/endpoints';

// Captured incoming member emails + the AI draft we would send, for review.
// Read-only: no reply/diff in this simple pilot.

interface RecordItem {
  id: number;
  status: string;
  is_holding_reply: boolean;
  subject?: string | null;
  from_email?: string | null;
  error?: string | null;
  received_at?: string | null;
  created_at?: string | null;
}

interface RecordsResponse {
  total: number;
  limit: number;
  offset: number;
  records: RecordItem[];
}

interface RecordDetail {
  id: number;
  status: string;
  gate_reason?: string | null;
  from_name?: string | null;
  from_email?: string | null;
  subject?: string | null;
  received_at?: string | null;
  clean_body?: string | null;
  has_attachments: boolean;
  ai_draft?: string | null;
  ai_actions?: string[] | null;
  is_holding_reply: boolean;
  draft_model?: string | null;
  draft_generated_at?: string | null;
  error?: string | null;
}

function StatusBadge({ status }: { status: string }) {
  const color =
    status === 'draft_failed'
      ? 'bg-red-100 text-red-700'
      : status === 'drafted'
        ? 'bg-blue-100 text-blue-700'
        : 'bg-gray-100 text-gray-600';
  return (
    <span className={`rounded px-1.5 py-0.5 text-xs font-medium ${color}`}>
      {status}
    </span>
  );
}

function EmailCapture() {
  const [records, setRecords] = useState<RecordItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [detail, setDetail] = useState<RecordDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const loadRecords = async () => {
    setLoading(true);
    try {
      const data: RecordsResponse = await axios.get(
        endpoints.emailCapture.records(0, 100),
      );
      setRecords(data.records);
      setTotal(data.total);
    } catch {
      toast.error('Failed to load records');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRecords();
  }, []);

  const selectRecord = async (id: number) => {
    setSelectedId(id);
    setDetailLoading(true);
    try {
      const data: RecordDetail = await axios.get(
        endpoints.emailCapture.record(id),
      );
      setDetail(data);
    } catch {
      toast.error('Failed to load record');
    } finally {
      setDetailLoading(false);
    }
  };

  return (
    <div className="flex h-screen flex-col">
      <div className="flex items-center justify-between border-b border-gray-200 bg-white px-6 py-3">
        <h1 className="text-lg font-semibold text-gray-900">
          Email Capture
          <span className="ml-2 text-sm font-normal text-gray-500">
            {total} captured
          </span>
        </h1>
        <button
          onClick={loadRecords}
          className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          <MdRefresh size={16} />
          Refresh
        </button>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* List */}
        <div className="w-80 shrink-0 overflow-y-auto border-r border-gray-200 bg-gray-50">
          {loading ? (
            <p className="p-4 text-sm text-gray-500">Loading…</p>
          ) : records.length === 0 ? (
            <p className="p-4 text-sm text-gray-500">No captured emails yet.</p>
          ) : (
            <ul>
              {records.map((r) => (
                <li key={r.id}>
                  <button
                    onClick={() => selectRecord(r.id)}
                    className={`w-full border-b border-gray-100 px-4 py-3 text-left hover:bg-white ${
                      selectedId === r.id ? 'bg-white' : ''
                    }`}
                  >
                    <div className="mb-1 flex items-center justify-between gap-2">
                      <StatusBadge status={r.status} />
                      {r.is_holding_reply && (
                        <span className="text-xs text-amber-600">holding</span>
                      )}
                    </div>
                    {r.status === 'capture_failed' ? (
                      <p className="truncate text-sm font-medium text-gray-500">
                        could not be stored — needs manual recovery
                      </p>
                    ) : (
                      <>
                        <p className="truncate text-sm font-medium text-gray-900">
                          {r.subject || '(no subject)'}
                        </p>
                        <p className="truncate text-xs text-gray-500">
                          {r.from_email || ''}
                        </p>
                      </>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Detail */}
        <div className="flex-1 overflow-y-auto p-6">
          {detailLoading ? (
            <p className="text-sm text-gray-500">Loading…</p>
          ) : !detail ? (
            <p className="text-sm text-gray-500">
              Select a record to see the incoming email and our AI draft.
            </p>
          ) : (
            <div className="mx-auto max-w-3xl space-y-6">
              <div className="flex items-center gap-3">
                <StatusBadge status={detail.status} />
                {detail.gate_reason && (
                  <span className="text-xs text-gray-400">
                    gate reason: {detail.gate_reason}
                  </span>
                )}
                {detail.draft_model && (
                  <span className="text-xs text-gray-400">
                    {detail.draft_model}
                  </span>
                )}
              </div>

              {/* Incoming email */}
              <section>
                <h3 className="mb-1 text-sm font-semibold text-gray-900">
                  Incoming email
                </h3>
                <div className="rounded-md border border-gray-200 bg-white p-3">
                  <p className="text-xs text-gray-500">
                    {detail.from_name || ''} &lt;{detail.from_email || ''}&gt;
                    {detail.has_attachments && ' · 📎 attachments'}
                  </p>
                  <p className="mt-1 text-sm font-medium text-gray-800">
                    {detail.subject || '(no subject)'}
                  </p>
                  <p className="mt-2 whitespace-pre-wrap break-words text-sm text-gray-700">
                    {detail.clean_body || '(empty)'}
                  </p>
                </div>
              </section>

              {/* AI draft */}
              <section>
                <h3 className="mb-1 text-sm font-semibold text-gray-900">
                  AI draft
                </h3>
                <div className="rounded-md border border-gray-200 bg-white p-3">
                  <p className="whitespace-pre-wrap break-words text-sm text-gray-700">
                    {detail.ai_draft || '(no draft yet)'}
                  </p>
                  {detail.ai_actions && detail.ai_actions.length > 0 && (
                    <div className="mt-2 border-t border-gray-100 pt-2">
                      <p className="text-xs font-medium text-amber-700">
                        Reviewer actions:
                      </p>
                      <ul className="list-disc pl-5 text-xs text-gray-600">
                        {detail.ai_actions.map((a, idx) => (
                          <li key={idx}>{a}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {detail.error && (
                    <p className="mt-2 text-xs text-red-600">{detail.error}</p>
                  )}
                </div>
              </section>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default EmailCapture;
